import { useMemo } from 'react'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { Status } from '@/core/types/utils'
import type { Option } from '@/core/components/ui/multi-select'
import { ratesQueryOptions } from '../rates-and-packages/rates/ratesQueryOptions'
import { packagesQueryOptions } from '../rates-and-packages/packages/packagesQueryOptions'
import { activitiesQueryOptions } from '../activities-and-products/activitiesQueryOptions'
import { cancellationPoliciesQueryOptions } from '../site-customization/policies/policiesQueryOptions'
import { activePolicies, type SeasonalAssignmentRow } from './seasonal.utils'

export type SeasonalTargetType = 'rate' | 'package' | 'activity'

/** Query options the screen needs besides the assignments list. The route
 * loader prefetches the same list so the lazy screen never suspends twice. */
export const seasonalCatalogQueries = (siteId: string, token: string) =>
  [
    ratesQueryOptions(siteId, token),
    packagesQueryOptions(siteId, token),
    activitiesQueryOptions(siteId, token),
    cancellationPoliciesQueryOptions(siteId, token),
  ] as const

/**
 * Everything the list and the form need to label and pick targets.
 *
 * `property/:id/rates` excludes packages server-side, so packages come from
 * their own endpoint; both are `rate` for the seasonal endpoints.
 */
export const useSeasonalCatalog = (siteId: string, token: string) => {
  const intl = useIntl()
  const [rates, packages, activities, policies] = useSuspenseQueries({
    queries: seasonalCatalogQueries(siteId, token),
  })

  return useMemo(() => {
    const rateGroup = intl.formatMessage({ id: 'seasonalPolicies.form.groupRates' })
    const packageGroup = intl.formatMessage({
      id: 'seasonalPolicies.form.groupPackages',
    })

    // Only Active entities can be targeted: a paused or archived rate is not
    // sold, so assigning a season to it is almost certainly a mistake.
    const rateOptions: Option[] = [
      ...rates.data.data
        .filter((rate) => rate.rateDetails.status === Status.Active)
        .map((rate) => ({
          value: rate.id,
          label: `${rate.rateDetails.rateCode} - ${rate.rateDetails.shortTitle}`,
          group: rateGroup,
        })),
      ...packages.data.data
        .filter(
          (pkg): pkg is typeof pkg & { id: string } =>
            Boolean(pkg.id) && pkg.packageDetails.status === Status.Active
        )
        .map((pkg) => ({
          value: pkg.id,
          label: `${pkg.packageDetails.rateCode} - ${pkg.packageDetails.shortTitle}`,
          group: packageGroup,
        })),
    ]

    const activityOptions: Option[] = activities.data.data
      .filter((activity) => activity.activityDetails.status === Status.Active)
      .map((activity) => ({
        value: activity.id,
        label: activity.activityDetails.shortTitle,
      }))

    // Built from the full package list (any status) so a row of a paused
    // package still shows the Package badge.
    const packageIds = new Set(
      packages.data.data.map((pkg) => pkg.id).filter(Boolean)
    )
    const targetType = (row: SeasonalAssignmentRow): SeasonalTargetType =>
      row.kind === 'activity'
        ? 'activity'
        : packageIds.has(row.entityId)
          ? 'package'
          : 'rate'

    return {
      rateOptions,
      activityOptions,
      policies: activePolicies(policies.data.data),
      targetType,
    }
  }, [rates.data, packages.data, activities.data, policies.data, intl])
}
